import { useMemo, useState } from "react";
import { useConnection, useAnchorWallet } from "@solana/wallet-adapter-react";
import { Program, AnchorProvider, Idl, web3 } from "@coral-xyz/anchor";
import idlJson from "../programs/dag.json";
import { Dag } from "../programs/dag";
import { Connection, Keypair, PublicKey, SendOptions, Transaction } from "@solana/web3.js";
import { createAssociatedTokenAccountIdempotentInstruction, getAssociatedTokenAddressSync, TOKEN_2022_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID } from "@solana/spl-token";
import { pinata } from "~/pinata";
import { promiseWithRetry } from "~/utils/promiseWithRetry";
import { BN } from '@coral-xyz/anchor';
import { TOKENS } from "~/constants";

const PROGRAM_ID = new PublicKey(idlJson.address);

const getProjectPda = (tokenAddress: string) => {
  const tokenMintPublicKey = new PublicKey(tokenAddress);
  const [pda] = PublicKey.findProgramAddressSync(
    [Buffer.from("project"), tokenMintPublicKey.toBuffer()],
    PROGRAM_ID
  );
  return pda;
}

const getUserLockPda = (owner: string, projectPda: PublicKey) => {
  const [pda] = PublicKey.findProgramAddressSync(
    [Buffer.from("user_lock"), new PublicKey(owner).toBuffer(), projectPda.toBuffer()],
    PROGRAM_ID
  );
  return pda;
}

const addOrSkipCreateUserTokenAccountIx = async (connection: Connection, user: PublicKey, tx: Transaction, token: typeof TOKENS[keyof typeof TOKENS]) => {
  const userTokenAccount = getAssociatedTokenAddressSync(
    new PublicKey(token.address),
    user,
    true,
    token.program
  );
  
  const accountInfo = await connection.getAccountInfo(userTokenAccount);
  if (!accountInfo) {
    const createUserTokenAccountIx =
      createAssociatedTokenAccountIdempotentInstruction(
        user,
        userTokenAccount,
        user,
        new PublicKey(token.address),
        token.program
      );
    tx.add(createUserTokenAccountIx);
  }

  return userTokenAccount;
}

export const useDagProgram = () => {
  const { connection } = useConnection();
  const wallet = useAnchorWallet();
  const [isPending, setIsPending] = useState(false);

  const program = useMemo(() => {
    if (!connection || !wallet) {
      return null;
    }

    try {
      const provider = new AnchorProvider(connection, wallet, { commitment: "confirmed" });
      const validIdl = idlJson as Idl;

      if (!validIdl || !validIdl.metadata || !validIdl.instructions) {
        console.error("useDagProgram: Provided IDL is invalid.");
        return null;
      }

      return new Program<Dag>(validIdl, provider);
    } catch (error) {
      console.error("useDagProgram: Failed to initialize program:", error);
      return null;
    }
  }, [connection, wallet]);

  const initializeProject = async ({
    name,
    symbol,
    description,
    icon
  }: {
    name: string;
    symbol: string;
    description: string;
    icon: string;
  }): Promise<{
    projectPda: PublicKey;
    tokenMint: Keypair;
    tx: string;
  }> => {
    if (!program || !wallet) {
      throw new Error("Program or wallet not initialized");
    }

    setIsPending(true);

    try {
      let metadataURI = ""
      await promiseWithRetry(async () => {
        const imageFile = new File([Buffer.from(icon.split(',')[1], 'base64')], "image.png", { type: "image/png" });
        const imageUpload = await pinata.upload.file(imageFile);
        const file = new File([`
          {
            "name": "${name}",
            "symbol": "${symbol}",
            "description": "${description}",
            "image": "https://gateway.pinata.cloud/ipfs/${imageUpload.IpfsHash}"
          }
        `], "metadata.json", { type: "application/json" });
        const upload = await pinata.upload.file(file);
        return`https://gateway.pinata.cloud/ipfs/${upload.IpfsHash}`
      }, 3, {
        onSuccess: (data) => {
          metadataURI = data
        },
      })

      const tokenMint = Keypair.generate();
      const [projectPda] = PublicKey.findProgramAddressSync(
        [Buffer.from("project"), tokenMint.publicKey.toBuffer()],
        program.programId
      );

      // Get the platform token mint address from constants
      const PLATFORM_TOKEN_MINT = new PublicKey("R1NGSAaSvUVFzNecDnisqW5c7HXKj7cjFukyRVL6xui");
      const PLATFORM_TOKEN_PROGRAM = new PublicKey("TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb");
      
      // Get associated token accounts
      const tokenVault = getAssociatedTokenAddressSync(
        tokenMint.publicKey,
        projectPda,
        true,
        TOKEN_2022_PROGRAM_ID
      );

      const platformTokensVault = getAssociatedTokenAddressSync(
        PLATFORM_TOKEN_MINT,
        projectPda,
        true,
        PLATFORM_TOKEN_PROGRAM
      );
      
      const tx = await program.methods
        .initializeProject(name, symbol, description, metadataURI)
        .accounts({
          creator: wallet.publicKey,
          tokenMint: tokenMint.publicKey,
          tokenVault: tokenVault,
          platformTokensVault: platformTokensVault,
          platformTokenMint: PLATFORM_TOKEN_MINT,
          project: projectPda,
          tokenProgram: TOKEN_2022_PROGRAM_ID,
          platformTokenProgram: PLATFORM_TOKEN_PROGRAM,
          associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID,
          systemProgram: web3.SystemProgram.programId,
          rent: web3.SYSVAR_RENT_PUBKEY,
        } as any)
        .signers([tokenMint])
        .rpc();

      return {
        projectPda,
        tokenMint,
        tx
      };
    } catch (error) {
      console.error("initializeProject: Failed to initialize project:", error);
      throw error;
    } finally {
      setIsPending(false);
    }
  }

  const createProposal = async ({
    title,
    description,
    tokenAddress,
    bountyBps,
    pushRequest = false
  }: {
    title: string;
    description: string;
    tokenAddress: string;
    bountyBps: number;
    pushRequest?: boolean;
  }): Promise<{
    projectPda: PublicKey;
    proposalPda: PublicKey;
    txSignature: string;
  }> => {
    if (!program || !wallet) {
      throw new Error("Program or wallet not initialized");
    }

    setIsPending(true);

    try {
      const projectPda = getProjectPda(tokenAddress);
      
      // Get the project account to read the next proposal ID
      const projectAccount = await program.account.project.fetch(projectPda);
      const proposalId = projectAccount.nextProposalId;
      
      // Derive the proposal PDA using the correct seeds
      const [proposalPda] = PublicKey.findProgramAddressSync(
        [Buffer.from("proposal"), projectPda.toBuffer(), proposalId.toBuffer('le', 8)],
        program.programId
      );

      // Create the transaction method
      const method = program.methods
        .createProposal(title, description, bountyBps, pushRequest)
        .accountsStrict({
          project: projectPda,
          proposal: proposalPda,
          creator: wallet.publicKey,
          systemProgram: web3.SystemProgram.programId,
        });

      // Try to send the transaction with retries
      let txSignature: string | undefined;
      let attempts = 0;
      const maxAttempts = 3;

      while (!txSignature && attempts < maxAttempts) {
        try {
          // Get a fresh blockhash before each attempt
          const latestBlockhash = await program.provider.connection.getLatestBlockhash();
          
          txSignature = await method.rpc({
            commitment: 'finalized',
            skipPreflight: false, // Enable preflight checks
            maxRetries: 3, // Allow some retries at the RPC level
            preflightCommitment: 'processed', // Use a lower commitment for preflight
          });

          // Wait for confirmation
          const confirmation = await program.provider.connection.confirmTransaction({
            signature: txSignature,
            blockhash: latestBlockhash.blockhash,
            lastValidBlockHeight: latestBlockhash.lastValidBlockHeight,
          }, 'finalized');

          if (confirmation.value.err) {
            throw new Error(`Transaction failed: ${confirmation.value.err}`);
          }

          console.log('Transaction confirmed:', {
            projectPda: projectPda.toBase58(),
            proposalPda: proposalPda.toBase58(),
            txSignature,
            slot: confirmation.context.slot
          });
        } catch (err) {
          attempts++;
          if (attempts >= maxAttempts) {
            throw err;
          }
          console.log(`Attempt ${attempts} failed, retrying...`, err);
          // Add a small delay before retrying
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      if (!txSignature) {
        throw new Error('Failed to send transaction after multiple attempts');
      }

      return {
        projectPda,
        proposalPda,
        txSignature
      };
    } catch (error) {
      console.error("createProposal: Failed to create proposal:", error);
      throw error;
    } finally {
      setIsPending(false);
    }
  }

  const buy = async ({
    token,
    tokenAmount,
    slippage
  }: {
    token: typeof TOKENS[keyof typeof TOKENS],
    tokenAmount: BN,
    slippage: number
  }) => {
    const buyerPublicKey = wallet!.publicKey;
    const projectPda = getProjectPda(token.address);
    const provider = program!.provider;
    const tokenVault = getAssociatedTokenAddressSync(
      new PublicKey(token.address),
      projectPda,
      true,
      token.program
    );

    setIsPending(true);
    try {
      const tx = new Transaction();

      //
      await addOrSkipCreateUserTokenAccountIx(connection, buyerPublicKey, tx, token);

      //
      const quote = await program!.methods
        .calculatePriceUsingVault(tokenAmount, false)
        .accounts({
          tokenVault: tokenVault,
        })
        .view();
      const purchaseIx = await program!.methods
        .purchaseTokens(tokenAmount, quote)
        .accounts({
          buyer: buyerPublicKey!,
          project: projectPda,
          tokenProgram: token.program,
        })
        .instruction();

      tx.add(purchaseIx);
      
      const signature = await provider!.sendAndConfirm!(tx, [], { commitment: "confirmed" } as SendOptions);
      console.log("Buy transaction successful:", signature);
      return signature;
    } catch (error) {
      console.error("Buy transaction failed:", error);
      return null;
    } finally {
      setIsPending(false);
    }
  }

  const sell = async ({
    token,
    tokenAmount,
    slippage
  }: {
    tokenAmount: BN,
    token: typeof TOKENS[keyof typeof TOKENS],
    slippage: number
  }) => {
    const sellerPublicKey = wallet!.publicKey;
    const projectPda = getProjectPda(token.address);
    const provider = program!.provider;
    const tokenVault = getAssociatedTokenAddressSync(
      new PublicKey(token.address),
      projectPda,
      true,
      token.program
    );

    setIsPending(true);
    try {
      const tx = new Transaction();

      //
      await addOrSkipCreateUserTokenAccountIx(connection, sellerPublicKey, tx, token);

      //
      const quote = await program!.methods
        .calculatePriceUsingVault(tokenAmount, true)
        .accounts({
          tokenVault: tokenVault,
        })
        .view();
      const purchaseIx = await program!.methods
        .sellTokens(tokenAmount, quote)
        .accounts({
          seller: sellerPublicKey!,
          project: projectPda,
          tokenProgram: token.program,
        })
        .instruction();
      tx.add(purchaseIx);

      const signature = await provider!.sendAndConfirm!(tx, [], { commitment: "confirmed" } as SendOptions);
      console.log("Sell transaction successful:", signature);
      return signature;
    } catch (error) {
      console.error("Sell transaction failed:", error);
      return null;
    } finally {
      setIsPending(false);
    }
  }

  const seed = async ({
    token,
  }: {
    token: typeof TOKENS[keyof typeof TOKENS],
  }) => {
    const projectPda = getProjectPda(token.address);
    const seeder = wallet!.publicKey;
    const provider = program!.provider;
    
    const ammConfig = new PublicKey(
      "9zSzfkYy6awexsHvmggeH36pfVUdDGyCcwmjT3AQPBj6"
    );

    setIsPending(true);
    try {
      const tx = new Transaction();
      const ix = await program!.methods
        .seedTokens()
        .accounts({
          project: projectPda,
          ammConfig,
          seeder,
          tokenProgram: token.program,
        })
        .preInstructions(
          [web3.ComputeBudgetProgram.setComputeUnitLimit({ units: 400_000 })]
        )
        .instruction();
      tx.add(ix);

      const signature = await provider!.sendAndConfirm!(tx, [], { commitment: "confirmed" } as SendOptions);
      console.log("Seed transaction successful:", signature);
      return signature;
    } catch (error) {
      console.error("Seed transaction failed:", error);
      return null;
    } finally {
      setIsPending(false);
    }
  }

  const lock = async ({
    token,
    tokenAmount,
    epoch,
  }: {
    token: typeof TOKENS[keyof typeof TOKENS],
    tokenAmount: BN,
    epoch: number
  }) => {
    const owner = wallet!.publicKey;
    const projectPda = getProjectPda(token.address);
    const userLockPda = getUserLockPda(owner.toBase58(), projectPda);
    const provider = program!.provider;
    const tokenVault = getAssociatedTokenAddressSync(
      new PublicKey(token.address),
      projectPda,
      true,
      token.program
    );
    
    setIsPending(true);
    try {
      const tx = new Transaction();

      //
      const userTokenAccount = await addOrSkipCreateUserTokenAccountIx(connection, owner, tx, token);

      //
      const ix1 = await program!.methods.createUserLock().accountsStrict({
        owner,
        userLock: userLockPda,
        project: projectPda,
        systemProgram: web3.SystemProgram.programId,
      }).instruction()
      tx.add(ix1);
      
      //
      const ix2 = await program!.methods
        .vest(tokenAmount, new BN(epoch))
        .accountsStrict({
            owner,
            userLock: userLockPda,
            project: projectPda,
            projectTokenAccount: tokenVault,
            ownerTokenAccount: userTokenAccount,
            tokenMint: new PublicKey(token.address),
            tokenProgram: token.program,
            systemProgram: web3.SystemProgram.programId,
        })
        .instruction();
      tx.add(ix2);

      const signature = await provider!.sendAndConfirm!(tx, [], { commitment: "confirmed" } as SendOptions);
      console.log("Lock transaction successful:", signature);
      return signature;
    } catch (error) {
      console.error("Lock transaction failed:", error);
      return null;
    } finally {
      setIsPending(false);
    }
  }

  const extendLock = async ({
    token,
    epoch,
  }: {
    token: typeof TOKENS[keyof typeof TOKENS],
    epoch: number
  }) => {

    const owner = wallet!.publicKey;
    const projectPda = getProjectPda(token.address);
    const [userLockPda] = web3.PublicKey.findProgramAddressSync(
      [Buffer.from("user_lock"), owner.toBuffer(), projectPda.toBuffer()],
      program!.programId
    );
    const provider = program!.provider;
    
    setIsPending(true);
    try {
      const tx = new Transaction();

      const epochLengthSeconds = 60 * 60 * 24 * 30;
      const currentEpoch = Math.floor(Math.ceil(Date.now() / 1000) / epochLengthSeconds);
      const extendToEpoch = new BN(currentEpoch + epoch); 

      const ix = await program!.methods
        .extendLock(extendToEpoch)
        .accountsStrict({
            owner,
            userLock: userLockPda,
            project: projectPda,
            systemProgram: web3.SystemProgram.programId,
        })
        .instruction();
      tx.add(ix);

      const signature = await provider!.sendAndConfirm!(tx, [], { commitment: "confirmed" } as SendOptions);
      console.log("Extend lock transaction successful:", signature);
      return signature;
    } catch (error) {
      console.error("Extend lock transaction failed:", error);
      return null;
    } finally {
      setIsPending(false);
    }
  }

  return {
    program,
    initializeProject,
    createProposal,
    buy,
    sell,
    seed,
    lock,
    extendLock,
    getUserLockPda,
    getProjectPda,
    isPending,
  }
};